// Pure helpers for {{citation:…}} / {{citationRef:…}} response markers.
// Kept free of component imports so they are unit-testable under jest
// (react-markdown is ESM-only and breaks jest transforms).

const CITATION_MARKER_RE = /\{\{citation(Ref)?:([^}]+)\}\}/g;

/** Matches the synthetic hrefs emitted by `replaceCitationMarkers`. */
const CITATION_HREF_RE = /^#cite(?:ref)?-(.+)$/;

/**
 * Percent-encode a marker id for use as a markdown link destination.
 * `encodeURIComponent` leaves parentheses alone, which would close the `(…)`
 * destination early, so encode those too. `decodeURIComponent` reverses this.
 */
function encodeMarkerId(id: string): string {
  return encodeURIComponent(id).replace(/\(/g, "%28").replace(/\)/g, "%29");
}

/**
 * Replace `{{citation:<id>}}` / `{{citationRef:<id>}}` markers with inline
 * markdown links on synthetic `#cite-…` fragment hrefs, which ChatMarkdown's
 * `a` renderer turns into clickable footnote-style superscript markers. A
 * fragment href keeps the marker inline within its paragraph (unlike
 * block-level chart segments) and survives react-markdown's URL sanitization.
 * `citationRef` is retained for backward-compat with stored conversations and
 * now renders identically to `citation`.
 *
 * The marker's id is carried through into the href so the marker can be
 * resolved back to the exact CitationTool call it names (see
 * `citationIdFromHref`). It must NOT be replaced by the marker's position in
 * the text: the model writes markers in the order it presents its findings,
 * which is independent of the order it called the CitationTool in, so a
 * positional match hands one figure another figure's source. The id is
 * percent-encoded so a marker id containing markdown-significant characters
 * still produces a parseable link destination.
 */
export function replaceCitationMarkers(md: string): string {
  return md.replace(
    CITATION_MARKER_RE,
    (_match, ref, id) =>
      `[†](#${ref ? "citeref" : "cite"}-${encodeMarkerId(id)})`
  );
}

/**
 * The citation id embedded in a synthetic `#cite-…` / `#citeref-…` href, or
 * undefined when the href is an ordinary link. Inverse of the encoding done by
 * `replaceCitationMarkers`.
 */
export function citationIdFromHref(href: string): string | undefined {
  const match = href.match(CITATION_HREF_RE);
  return match ? decodeURIComponent(match[1]) : undefined;
}

// Mirrors CitationToolResults in RangerChat/toolDisplays.tsx (the artifact the
// CitationTool emits) — only the fields the marker needs.
export type CitationResults = {
  citationType?: string;
  /** The id of the cited source — a Notes.id for note citations. */
  citationId?: number;
  citationDescription?: string;
  documentId?: number;
  supportingText?: string;
  page?: number;
  pageEnd?: number;
};

/**
 * Deep-link to the cited source so the "Source" marker is clickable:
 *  - documentExerpt → the document reader at the excerpt (quote highlight +
 *    page range fallback), same URL contract as DocumentLink in
 *    RangerChat/toolDisplays.tsx.
 *  - note → the notes page filtered to the single cited note (`?noteId=`),
 *    which GlobalNotes turns into an `ids` filter so the note loads reliably
 *    regardless of paging or active search filters.
 * Undefined when there is no destination to build (e.g. inlineDocument, or a
 * note citation missing its id).
 */
export function citationHref(results: CitationResults): string | undefined {
  if (results.citationType === "documentExerpt" && results.documentId) {
    const params = new URLSearchParams();
    if (results.supportingText) {
      params.set("quote", results.supportingText.slice(0, 300));
    }
    if (results.page) params.set("page", String(results.page));
    if (results.page && results.pageEnd && results.pageEnd > results.page) {
      params.set("pageEnd", String(results.pageEnd));
    }
    const queryString = params.toString();
    return `/documents/${results.documentId}${queryString ? `?${queryString}` : ""}`;
  }
  if (results.citationType === "note" && results.citationId) {
    return `/notes?noteId=${results.citationId}`;
  }
  return undefined;
}
